import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery, useMutation } from '@tanstack/react-query';
import { createClient } from '@/lib/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Icons } from '@/components/ui/icons';

const supabase = createClient();

export function FirstLoginForm() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [step, setStep] = useState<'email' | 'password'>('email');
    const router = useRouter();

    // Vérifier si l'email existe
    // Vérifier si l'email existe
    const checkEmailMutation = useMutation({
        mutationFn: async (email: string) => {
            // 1. Vérifier le statut first_login
            const { data: isFirstLogin, error: checkError } = await supabase
                .rpc('check_email_for_first_login', { check_email: email });

            if (checkError) throw checkError;
            if (!isFirstLogin) {
                throw new Error('Ce compte est déjà configuré ou n\'existe pas');
            }

        },
        onSuccess: () => {
            setStep('password');
            toast.success('Email vérifié avec succès');
        },
        onError: (error) => {
            toast.error(error instanceof Error ? error.message : "Une erreur est survenue");
        }
    });
    const setPasswordMutation = useMutation({
        mutationFn: async ({ email, password }: { email: string; password: string }) => {
            const { data: updated, error: rpcError } = await supabase
                .rpc('set_first_password', {
                    user_email: email,
                    new_password: password
                });

            console.log(updated, rpcError)
            if (rpcError) throw rpcError;
            if (!updated) throw new Error("La mise à jour du mot de passe a échoué");

            const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            window.progressier.add({
                id: authData.id,
                email: email,
              });

            if (signInError) throw signInError;
            return authData;
        },
        onSuccess: async (data) => {
            const roles = data.user?.user_metadata?.roles || [];
            const redirectPath = roles.includes('admin') ? '/dashboard' : '/driver';
            router.push(redirectPath);
            toast.success('Mot de passe défini avec succès');
        },
        onError: (error: any) => {
            toast.error(error.message || "Une erreur est survenue lors de la mise à jour du mot de passe");
        }
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (step === 'email') {
            checkEmailMutation.mutate(email);
        } else {
            if (password !== confirmPassword) {
                toast.error('Les mots de passe ne correspondent pas');
                return;
            }
            setPasswordMutation.mutate({ email, password });
        }
    };

    return (
        <Card className="w-full max-w-md mx-auto">
            <CardHeader>
                <CardTitle>{step === 'email' ? 'Première connexion' : 'Définir votre mot de passe'}</CardTitle>
                <CardDescription>
                    {step === 'email'
                        ? 'Veuillez entrer votre adresse email pour commencer'
                        : 'Choisissez un mot de passe sécurisé pour votre compte'
                    }
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                    {step === 'email' ? (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Adresse email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="nom@exemple.com"
                                    className="bg-background"
                                    required
                                    autoFocus
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={checkEmailMutation.isPending}
                            >
                                {checkEmailMutation.isPending && (
                                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Vérifier l'email
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password">Nouveau mot de passe</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="bg-background"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="bg-background"
                                    required
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={setPasswordMutation.isPending}
                            >
                                {setPasswordMutation.isPending && (
                                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Définir le mot de passe
                            </Button>
                        </div>
                    )}
                </form>
            </CardContent>
        </Card>
    );
}